import React, { createContext, useContext, useEffect, useState } from 'react';
// import api from '@/lib/api';
import { useWorkspace } from '@/contexts/WorkspaceContext';

interface ThemeData {
  logoUrl?: string;
  primaryColor: string;
  secondaryColor: string;
  cssVars: {
    '--bp-primary': string;
    '--bp-secondary': string;
  };
}

interface ThemeContextType {
  theme: ThemeData;
  isLoading: boolean;
  error: string | null;
  refreshTheme: () => Promise<void>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Default BillPortal theme
const defaultTheme: ThemeData = {
  primaryColor: '#0ea5a3',
  secondaryColor: '#7c3aed',
  cssVars: {
    '--bp-primary': '#0ea5a3',
    '--bp-secondary': '#7c3aed'
  }
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<ThemeData>(defaultTheme);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const workspaceContext = useWorkspace();
  const workspace = workspaceContext?.currentWorkspace || null;

  const applyTheme = (themeData: ThemeData) => {
    // Apply CSS variables to document root
    const root = document.documentElement;
    
    Object.entries(themeData.cssVars).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });

    // Update favicon if logo URL is provided
    if (themeData.logoUrl) {
      const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (favicon) {
        favicon.href = themeData.logoUrl;
      }
    }

    setTheme(themeData);
  };

  const fetchTheme = async () => {
    // Temporarily disable theme API calls to avoid hitting /v1/workspaces.
    // Apply default theme instantly. When a partner theme API is provided,
    // this function can be updated to fetch it without using workspace endpoints.
    setIsLoading(true);
    setError(null);
    applyTheme(defaultTheme);
    setIsLoading(false);
  };

  const refreshTheme = async () => {
    await fetchTheme();
  };

  useEffect(() => {
    fetchTheme();
  }, [workspace?.id]);

  const value: ThemeContextType = {
    theme,
    isLoading,
    error,
    refreshTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// CSS variables for Tailwind integration
export const themeCSS = `
  :root {
    --bp-primary: var(--bp-primary, #0ea5a3);
    --bp-secondary: var(--bp-secondary, #7c3aed);
  }
  
  .bg-primary {
    background-color: var(--bp-primary);
  }
  
  .text-primary {
    color: var(--bp-primary);
  }
  
  .border-primary {
    border-color: var(--bp-primary);
  }
  
  .bg-secondary {
    background-color: var(--bp-secondary);
  }
  
  .text-secondary {
    color: var(--bp-secondary);
  }
  
  .border-secondary {
    border-color: var(--bp-secondary);
  }
  
  .hover\\:bg-primary:hover {
    background-color: var(--bp-primary);
  }
  
  .hover\\:text-primary:hover {
    color: var(--bp-primary);
  }
  
  .focus\\:ring-primary:focus {
    --tw-ring-color: var(--bp-primary);
  }
`;

// Inject theme CSS into document head
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = themeCSS;
  document.head.appendChild(style);
}
